/**
 * Dynamic Date Variables Utility
 * 
 * This utility provides functions for handling dynamic date variables in workflow inputs.
 * It supports variables like ~today~, ~yesterday~, ~lbd~ (last business day), etc.
 */

/**
 * List of supported dynamic date variables
 */
export const DYNAMIC_DATE_VARIABLES = [
  {
    key: 'today',
    label: 'Today',
    description: 'Current date',
    category: 'current'
  },
  {
    key: 'yesterday',
    label: 'Yesterday', 
    description: 'Previous day',
    category: 'past'
  },
  {
    key: 'tomorrow',
    label: 'Tomorrow',
    description: 'Next day',
    category: 'future'
  },
  {
    key: 'lbd',
    label: 'Last Business Day',
    description: 'Most recent business day (excludes weekends)',
    category: 'business'
  },
  {
    key: 'nbd',
    label: 'Next Business Day',
    description: 'Next business day (excludes weekends)',
    category: 'business'
  },
  {
    key: 'sow',
    label: 'Start of Week',
    description: 'Monday of current week',
    category: 'periods'
  },
  {
    key: 'eow',
    label: 'End of Week',
    description: 'Friday of current week',
    category: 'periods'
  },
  {
    key: 'som',
    label: 'Start of Month',
    description: 'First day of current month',
    category: 'periods'
  },
  {
    key: 'eom',
    label: 'End of Month',
    description: 'Last day of current month',
    category: 'periods'
  },
  {
    key: 'soq',
    label: 'Start of Quarter',
    description: 'First day of current quarter',
    category: 'periods'
  },
  {
    key: 'eoq',
    label: 'End of Quarter',
    description: 'Last day of current quarter',
    category: 'periods'
  },
  {
    key: 'soy',
    label: 'Start of Year',
    description: 'January 1st of current year',
    category: 'periods'
  },
  {
    key: 'eoy',
    label: 'End of Year',
    description: 'December 31st of current year',
    category: 'periods'
  }
]

// Add 10 commonly used additional variables
DYNAMIC_DATE_VARIABLES.push(
  // Last/Next week boundaries
  { key: 'slw', label: 'Start of Last Week', description: 'Monday of last week', category: 'periods' },
  { key: 'elw', label: 'End of Last Week', description: 'Friday of last week', category: 'periods' },
  { key: 'snw', label: 'Start of Next Week', description: 'Monday of next week', category: 'periods' },
  { key: 'enw', label: 'End of Next Week', description: 'Friday of next week', category: 'periods' },
  // Previous/Next month boundaries
  { key: 'spm', label: 'Start of Previous Month', description: 'First day of previous month', category: 'periods' },
  { key: 'epm', label: 'End of Previous Month', description: 'Last day of previous month', category: 'periods' },
  { key: 'snm', label: 'Start of Next Month', description: 'First day of next month', category: 'periods' },
  { key: 'enm', label: 'End of Next Month', description: 'Last day of next month', category: 'periods' },
  // Previous quarter boundaries
  { key: 'spq', label: 'Start of Previous Quarter', description: 'First day of previous quarter', category: 'periods' },
  { key: 'epq', label: 'End of Previous Quarter', description: 'Last day of previous quarter', category: 'periods' },
  // Weekdays this/next/last
  { key: 'mon', label: 'This Monday', description: 'Monday of this week', category: 'weekdays' },
  { key: 'tue', label: 'This Tuesday', description: 'Tuesday of this week', category: 'weekdays' },
  { key: 'wed', label: 'This Wednesday', description: 'Wednesday of this week', category: 'weekdays' },
  { key: 'thu', label: 'This Thursday', description: 'Thursday of this week', category: 'weekdays' },
  { key: 'fri', label: 'This Friday', description: 'Friday of this week', category: 'weekdays' },
  { key: 'sat', label: 'This Saturday', description: 'Saturday of this week', category: 'weekdays' },
  { key: 'sun', label: 'This Sunday', description: 'Sunday of this week', category: 'weekdays' },
  { key: 'nmon', label: 'Next Monday', description: 'Monday of next week', category: 'weekdays' },
  { key: 'nfri', label: 'Next Friday', description: 'Friday of next week', category: 'weekdays' },
  { key: 'lmon', label: 'Last Monday', description: 'Monday of last week', category: 'weekdays' },
  { key: 'lfri', label: 'Last Friday', description: 'Friday of last week', category: 'weekdays' },
  // To-Date ranges
  { key: 'wtd', label: 'Week To Date', description: 'From start of week to today', category: 'to_date' },
  { key: 'mtd', label: 'Month To Date', description: 'From start of month to today', category: 'to_date' },
  { key: 'qtd', label: 'Quarter To Date', description: 'From start of quarter to today', category: 'to_date' },
  { key: 'ytd', label: 'Year To Date', description: 'From start of year to today', category: 'to_date' },
  // Relative offsets
  { key: 'd7', label: 'In 7 Days', description: '7 days from today', category: 'future' },
  { key: 'd14', label: 'In 14 Days', description: '14 days from today', category: 'future' },
  { key: 'd-7', label: '7 Days Ago', description: '7 days before today', category: 'past' },
  { key: 'd-14', label: '14 Days Ago', description: '14 days before today', category: 'past' },
  // Rolling ranges (expressed as single value anchors; UI can use two variables)
  { key: 'last_7_days_start', label: 'Last 7 Days (Start)', description: '6 days before yesterday', category: 'ranges' },
  { key: 'last_7_days_end', label: 'Last 7 Days (End)', description: 'Yesterday', category: 'ranges' },
  { key: 'last_30_days_start', label: 'Last 30 Days (Start)', description: '29 days before yesterday', category: 'ranges' },
  { key: 'last_30_days_end', label: 'Last 30 Days (End)', description: 'Yesterday', category: 'ranges' },
)

/**
 * Get dynamic date variables grouped by category
 */
export const getDynamicDateVariablesByCategory = () => {
  const categories = {
    current: { label: 'Current', variables: [] },
    past: { label: 'Past', variables: [] },
    future: { label: 'Future', variables: [] },
    business: { label: 'Business Days', variables: [] },
    periods: { label: 'Periods', variables: [] },
    weekdays: { label: 'Weekdays', variables: [] },
    to_date: { label: 'To-Date', variables: [] },
    ranges: { label: 'Ranges', variables: [] }
  }

  DYNAMIC_DATE_VARIABLES.forEach(variable => {
    if (categories[variable.category]) {
      categories[variable.category].variables.push(variable)
    }
  })

  return categories
}

/**
 * Check if a string contains dynamic date variables
 */
export const containsDynamicDateVariables = (text) => {
  if (!text || typeof text !== 'string') return false
  return /~[a-zA-Z0-9_-]+~/.test(text)
}

/**
 * Extract all dynamic date variables from a string
 */
export const extractDynamicDateVariables = (text) => {
  if (!text || typeof text !== 'string') return []
  const matches = text.match(/~([a-zA-Z0-9_-]+)~/g) || []
  return matches.map(match => match.slice(1, -1)) // Remove ~ characters
}

/**
 * Check if a variable key is a valid dynamic date variable
 */
export const isValidDynamicDateVariable = (key) => {
  return DYNAMIC_DATE_VARIABLES.some(variable => variable.key === key)
}

/**
 * Calculate the actual date for a dynamic date variable
 */
export const calculateDynamicDate = (variableKey, baseDate = new Date()) => {
  const date = new Date(baseDate)
  
  switch (variableKey) {
    case 'today':
      return date
    
    case 'yesterday':
      date.setDate(date.getDate() - 1)
      return date
    
    case 'tomorrow':
      date.setDate(date.getDate() + 1)
      return date
    
    case 'sow': // Start of Week (Monday)
    case 'start_of_week':
      return getStartOfWeek(date)
    
    case 'eow': // End of Week (Friday)
    case 'end_of_week':
      return getEndOfWeek(date)
    
    case 'nbd':
    case 'next_business_day':
      return getNextBusinessDay(date)
    
    case 'lbd':
    case 'last_business_day':
      return getLastBusinessDay(date)
    
    case 'som':
    case 'start_of_month':
      return new Date(date.getFullYear(), date.getMonth(), 1)
    
    case 'eom':
    case 'end_of_month':
      return new Date(date.getFullYear(), date.getMonth() + 1, 0)
    
    case 'soq':
    case 'start_of_quarter':
      return getStartOfQuarter(date)
    
    case 'eoq':
    case 'end_of_quarter':
      return getEndOfQuarter(date)
    
    case 'soy': // Start of Year
      return new Date(date.getFullYear(), 0, 1)
    
    case 'eoy': // End of Year
      return new Date(date.getFullYear(), 11, 31)
    
    // Weekdays this week
    case 'mon': return getWeekdayOfThisWeek(date, 1)
    case 'tue': return getWeekdayOfThisWeek(date, 2)
    case 'wed': return getWeekdayOfThisWeek(date, 3)
    case 'thu': return getWeekdayOfThisWeek(date, 4)
    case 'fri': return getWeekdayOfThisWeek(date, 5)
    case 'sat': return getWeekdayOfThisWeek(date, 6)
    case 'sun': return getWeekdayOfThisWeek(date, 0)

    // Next/Last key weekdays
    case 'nmon': return getWeekdayOfThisWeek(addDays(date, 7), 1)
    case 'nfri': return getWeekdayOfThisWeek(addDays(date, 7), 5)
    case 'lmon': return getWeekdayOfThisWeek(addDays(date, -7), 1)
    case 'lfri': return getWeekdayOfThisWeek(addDays(date, -7), 5)

    // To-Date anchors (return end date = today; UI uses with sow/som/soq/soy)
    case 'wtd': return date
    case 'mtd': return date
    case 'qtd': return date
    case 'ytd': return date

    // Relative offsets
    case 'd7': return addDays(date, 7)
    case 'd14': return addDays(date, 14)
    case 'd-7': return addDays(date, -7)
    case 'd-14': return addDays(date, -14)

    // Rolling ranges anchors
    case 'last_7_days_start': return addDays(date, -7)
    case 'last_7_days_end': return addDays(date, -1)
    case 'last_30_days_start': return addDays(date, -30)
    case 'last_30_days_end': return addDays(date, -1)

    // Start/End of Last Week
    case 'slw':
      return getStartOfWeek(addDays(date, -7))
    case 'elw':
      return getEndOfWeek(addDays(date, -7))

    // Start/End of Next Week
    case 'snw':
      return getStartOfWeek(addDays(date, 7))
    case 'enw':
      return getEndOfWeek(addDays(date, 7))

    // Start/End of Previous Month
    case 'spm':
      return new Date(date.getFullYear(), date.getMonth() - 1, 1)
    case 'epm':
      return new Date(date.getFullYear(), date.getMonth(), 0)

    // Start/End of Next Month
    case 'snm':
      return new Date(date.getFullYear(), date.getMonth() + 1, 1)
    case 'enm':
      return new Date(date.getFullYear(), date.getMonth() + 2, 0)

    // Start/End of Previous Quarter
    case 'spq':
      return getStartOfPreviousQuarter(date)
    case 'epq':
      return getEndOfPreviousQuarter(date)

    default:
      return date
  }
}

/**
 * Get the last business day (excluding weekends)
 */
const getLastBusinessDay = (date) => {
  const result = new Date(date)
  result.setDate(result.getDate() - 1)
  
  // If it's Saturday (6), go back to Friday
  if (result.getDay() === 6) {
    result.setDate(result.getDate() - 1)
  }
  // If it's Sunday (0), go back to Friday
  else if (result.getDay() === 0) {
    result.setDate(result.getDate() - 2)
  }
  
  return result
}

/**
 * Get the next business day (excluding weekends)
 */
const getNextBusinessDay = (date) => {
  const result = new Date(date)
  result.setDate(result.getDate() + 1)
  
  // If it's Saturday (6), move to Monday
  if (result.getDay() === 6) {
    result.setDate(result.getDate() + 2)
  }
  // If it's Sunday (0), move to Monday
  else if (result.getDay() === 0) {
    result.setDate(result.getDate() + 1)
  }
  
  return result
}

/**
 * Get start of week (Monday)
 */
const getStartOfWeek = (date) => {
  const result = new Date(date)
  const day = result.getDay()
  const diff = result.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
  result.setDate(diff)
  return result
}

/**
 * Get end of week (Friday)
 */
const getEndOfWeek = (date) => {
  const result = new Date(date)
  const day = result.getDay()
  const diff = result.getDate() - day + (day === 0 ? -2 : 5) // Adjust when day is Sunday
  result.setDate(diff)
  return result
}

/**
 * Get start of quarter
 */
const getStartOfQuarter = (date) => {
  const result = new Date(date)
  const quarter = Math.floor(result.getMonth() / 3)
  return new Date(result.getFullYear(), quarter * 3, 1)
}

/**
 * Get end of quarter
 */
const getEndOfQuarter = (date) => {
  const result = new Date(date)
  const quarter = Math.floor(result.getMonth() / 3)
  return new Date(result.getFullYear(), quarter * 3 + 3, 0)
}

/** Utility: add days */
const addDays = (date, days) => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

/**
 * Get start of previous quarter
 */
const getStartOfPreviousQuarter = (date) => {
  const d = new Date(date)
  const currentQuarter = Math.floor(d.getMonth() / 3)
  let year = d.getFullYear()
  let quarterIndex = currentQuarter - 1
  if (quarterIndex < 0) {
    quarterIndex = 3
    year -= 1
  }
  return new Date(year, quarterIndex * 3, 1)
}

/**
 * Get end of previous quarter
 */
const getEndOfPreviousQuarter = (date) => {
  const start = getStartOfPreviousQuarter(date)
  return new Date(start.getFullYear(), start.getMonth() + 3, 0)
}

/** Get weekday of the same ISO week (0=Sun..6=Sat target) */
const getWeekdayOfThisWeek = (date, targetDay) => {
  const base = getStartOfWeek(date)
  const d = new Date(base)
  // getStartOfWeek returns Monday
  // targetDay: 1=Mon..6=Sat, 0=Sun
  const offset = targetDay === 0 ? 6 : targetDay - 1
  d.setDate(d.getDate() + offset)
  return d
}

/**
 * Format date as YYYY-MM-DD
 */
export const formatDateAsISO = (date) => {
  if (!date) return ''
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format date for display
 */
export const formatDateForDisplay = (date) => {
  if (!date) return ''
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/** Preset ids shared with Laravel WorkflowExecutionService */
export const DEFAULT_DYNAMIC_DATE_FORMAT = 'iso'

export const DYNAMIC_DATE_FORMAT_PRESETS = [
  {
    id: 'iso',
    label: 'ISO (YYYY-MM-DD)',
    description: 'Common for APIs and query params',
  },
  {
    id: 'us_padded',
    label: 'US (MM/DD/YYYY)',
    description: 'Padded month and day',
  },
  {
    id: 'eu',
    label: 'EU (DD/MM/YYYY)',
    description: 'Day first',
  },
  {
    id: 'display',
    label: 'Display (Mon D, YYYY)',
    description: 'Short month name',
  },
]

/**
 * Normalize legacy callers that passed 'iso' / 'display' before preset ids existed.
 * @param {string} presetId
 * @returns {string}
 */
export const normalizeDynamicDateFormatPreset = (presetId) => {
  if (!presetId || typeof presetId !== 'string') return DEFAULT_DYNAMIC_DATE_FORMAT
  if (presetId === 'legacy_server') return 'iso'
  const allowed = DYNAMIC_DATE_FORMAT_PRESETS.map((p) => p.id)
  if (allowed.includes(presetId)) return presetId
  if (presetId === 'display') return 'display'
  return DEFAULT_DYNAMIC_DATE_FORMAT
}

/**
 * Format a date for preview/runtime substitution (must match PHP presets).
 * @param {Date} date
 * @param {string} presetId
 */
export const formatDateByPreset = (date, presetId) => {
  const preset = normalizeDynamicDateFormatPreset(presetId)
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''

  switch (preset) {
    case 'iso':
      return formatDateAsISO(d)
    case 'us_padded': {
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const year = d.getFullYear()
      return `${month}/${day}/${year}`
    }
    case 'eu': {
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    }
    case 'display':
      return formatDateForDisplay(d)
    default:
      return formatDateAsISO(d)
  }
}

/**
 * Replace dynamic date variables in a string with calculated dates
 * @param {string} text
 * @param {string} [formatPreset] preset id (default iso)
 * @param {Date} [baseDate]
 */
export const replaceDynamicDateVariables = (
  text,
  formatPreset = DEFAULT_DYNAMIC_DATE_FORMAT,
  baseDate = new Date(),
) => {
  if (!text || typeof text !== 'string') return text

  const preset = normalizeDynamicDateFormatPreset(formatPreset)

  let result = text
  const variables = extractDynamicDateVariables(text)

  variables.forEach((variableKey) => {
    if (isValidDynamicDateVariable(variableKey)) {
      const calculatedDate = calculateDynamicDate(variableKey, baseDate)
      const formattedDate = formatDateByPreset(calculatedDate, preset)

      result = result.replace(new RegExp(`~${variableKey}~`, 'g'), formattedDate)
    }
  })

  return result
}

/**
 * Preview what a text with dynamic date variables would look like
 * @param {string} text
 * @param {Date} [baseDate]
 * @param {string} [formatPreset] preset id
 */
export const previewDynamicDateText = (
  text,
  baseDate = new Date(),
  formatPreset = DEFAULT_DYNAMIC_DATE_FORMAT,
) => {
  return replaceDynamicDateVariables(text, formatPreset, baseDate)
}